import { useEffect, useMemo, useState, useCallback } from 'react';
import { apiFetch } from '../../utils/streamApi';
import IndustrialLayout from './IndustrialLayout';

// ── Access control: per-camera authorised-persons whitelist + (now-enforced)
// per-person time windows. Reads/writes real config via the existing
// PUT /cameras/{idx} deep-merge on detector_config.unknown:
//   allowed_names    : string[]                         (ENFORCED by UnknownPersonDetector)
//   allowed_schedule : { name: { days:number[], start, end } }  (SAVED, NOT enforced yet)
// Writing allowed_names goes live immediately — this is configuration, not a
// change to detection logic.

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DEFAULT_WINDOW = { days: [0, 1, 2, 3, 4, 5, 6], start: '09:00', end: '18:00' };

export default function IndustrialAccessPage() {
  const [cameras, setCameras] = useState([]);
  const [persons, setPersons] = useState([]);     // enrolled known faces (picker)
  const [scope, setScope]     = useState(null);   // selected camera idx
  const [names, setNames]     = useState([]);     // allowed_names for scope
  const [schedule, setSched]  = useState({});     // allowed_schedule for scope
  const [picker, setPicker]   = useState('');     // dropdown / free-text value
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);
  const [dirty, setDirty]     = useState(false);

  // Load cameras + enrolled persons once.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [cr, pr] = await Promise.all([
          apiFetch('/ui/cameras').then(r => r.json()),
          apiFetch('/ui/faces/known?page=0&per_page=500').then(r => r.ok ? r.json() : { persons: [] }),
        ]);
        if (!live) return;
        const cams = cr.cameras || [];
        setCameras(cams);
        setPersons((pr.persons || []).map(p => p.name).filter(Boolean));
        if (cams.length) selectCamera(cams[0].idx, cams);
      } catch {
        if (live) setMsg({ type: 'error', text: 'Failed to load cameras / persons' });
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectCamera = useCallback((idx, camList) => {
    const cams = camList || cameras;
    const cam  = cams.find(c => c.idx === idx);
    const unk  = cam?.detector_config?.unknown || {};
    setScope(idx);
    setNames(Array.isArray(unk.allowed_names) ? [...unk.allowed_names] : []);
    setSched(unk.allowed_schedule && typeof unk.allowed_schedule === 'object' ? { ...unk.allowed_schedule } : {});
    setPicker('');
    setDirty(false);
    setMsg(null);
  }, [cameras]);

  const addPerson = () => {
    const n = picker.trim();
    if (!n) return;
    if (names.some(x => x.toLowerCase() === n.toLowerCase())) { setPicker(''); return; }
    setNames([...names, n]);
    setPicker('');
    setDirty(true);
  };

  const removePerson = (n) => {
    setNames(names.filter(x => x !== n));
    setSched(s => { const c = { ...s }; delete c[n]; return c; });
    setDirty(true);
  };

  const toggleWindow = (n) => {
    setSched(s => {
      const c = { ...s };
      if (c[n]) delete c[n];
      else c[n] = { ...DEFAULT_WINDOW };
      return c;
    });
    setDirty(true);
  };

  const setWin = (n, patch) => {
    setSched(s => ({ ...s, [n]: { ...(s[n] || DEFAULT_WINDOW), ...patch } }));
    setDirty(true);
  };

  const toggleDay = (n, di) => {
    setSched(s => {
      const w = s[n] || DEFAULT_WINDOW;
      const days = w.days.includes(di) ? w.days.filter(d => d !== di) : [...w.days, di].sort((a, b) => a - b);
      return { ...s, [n]: { ...w, days } };
    });
    setDirty(true);
  };

  const save = async () => {
    if (scope == null) return;
    setSaving(true); setMsg(null);
    try {
      // Only include schedule entries for names still on the list.
      const cleanSched = {};
      names.forEach(n => { if (schedule[n]) cleanSched[n] = schedule[n]; });
      const r = await apiFetch(`/cameras/${scope}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ detector_config: { unknown: { allowed_names: names, allowed_schedule: cleanSched } } }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      // reflect saved state back into local camera cache
      setCameras(cs => cs.map(c => c.idx === scope
        ? { ...c, detector_config: { ...(c.detector_config || {}), unknown: { ...(c.detector_config?.unknown || {}), allowed_names: names, allowed_schedule: cleanSched } } }
        : c));
      setDirty(false);
      setMsg({ type: 'success', text: 'Saved — camera will reload with the new whitelist.' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const camName = cameras.find(c => c.idx === scope)?.name || (scope != null ? `Camera ${scope}` : '');
  const available = useMemo(
    () => persons.filter(p => !names.some(n => n.toLowerCase() === p.toLowerCase())),
    [persons, names]
  );
  const whitelistOn = names.length > 0;

  return (
    <IndustrialLayout
      eyebrow="Access control"
      title="Authorised persons"
      subtitle={loading ? 'LOADING…' : `${cameras.length} CAMERAS · ${persons.length} ENROLLED`}
      banner={
        <div className="ind-banner info">
          <b style={{ marginRight: 6 }}>Whitelist mode:</b>
          when a camera has any authorised person listed, <u>everyone else</u> — unknown people <em>and</em> known people
          not on the list — triggers a RESTRICTED alert. A person with a <b>time window</b> is allowed only during it;
          seen <b>outside their hours</b> they trigger RESTRICTED too.
        </div>
      }>
      {loading ? <div className="ind-empty">Loading…</div> : (
        <>
          {/* camera selector */}
          <div className="ind-card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span className="ind-eye" style={{ margin: 0 }}>Camera</span>
            <select value={scope ?? ''} onChange={e => { if (dirty && !window.confirm('Discard unsaved changes?')) return; selectCamera(Number(e.target.value)); }}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 13 }}>
              {cameras.map(c => <option key={c.idx} value={c.idx}>🎥 {c.name || `Camera ${c.idx}`}</option>)}
            </select>
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--ind-mono)', fontSize: 11, color: whitelistOn ? 'var(--ind-crit)' : 'var(--ind-tx3)' }}>
              {whitelistOn ? `WHITELIST ACTIVE · ${names.length} allowed` : 'OPEN · no whitelist (only unknowns alert)'}
            </span>
          </div>

          {/* add person */}
          <div className="ind-card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input list="known-persons" value={picker} onChange={e => setPicker(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addPerson(); }}
              placeholder="Add authorised person (pick enrolled or type a name)…"
              style={{ flex: 1, minWidth: 220, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 13 }} />
            <datalist id="known-persons">
              {available.map(p => <option key={p} value={p} />)}
            </datalist>
            <button className="ind-btn" onClick={addPerson} disabled={!picker.trim()}>+ Add</button>
          </div>

          {/* whitelist */}
          <div className="ind-rail">Authorised on {camName}</div>
          {!names.length && <div className="ind-empty">No authorised persons — this camera alerts on unknown people only.</div>}

          {names.map(n => {
            const win = schedule[n];
            const enrolled = persons.some(p => p.toLowerCase() === n.toLowerCase());
            return (
              <div key={n} className="ind-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="ic" style={{ width: 34, height: 34, borderRadius: 8, background: 'var(--ind-wash)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🧑</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b style={{ fontSize: 14 }}>{n}</b>
                    <div style={{ fontFamily: 'var(--ind-mono)', fontSize: 10.5, color: enrolled ? 'var(--ind-ok)' : 'var(--ind-high)' }}>
                      {enrolled ? 'enrolled face' : 'not enrolled — name-only match'}
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ind-tx2)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!win} onChange={() => toggleWindow(n)} /> Time window
                  </label>
                  <button className="ind-btn" onClick={() => removePerson(n)}>Remove</button>
                </div>

                {win && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--ind-line)', display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {DAYS.map((d, di) => (
                        <button key={d} onClick={() => toggleDay(n, di)}
                          style={{ width: 34, padding: '5px 0', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                            border: '1px solid var(--ind-line)',
                            background: win.days.includes(di) ? 'var(--ind-accent)' : 'var(--ind-ink)',
                            color: win.days.includes(di) ? '#fff' : 'var(--ind-tx2)' }}>{d}</button>
                      ))}
                    </div>
                    <label style={{ fontSize: 12, color: 'var(--ind-tx2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      From <input type="time" value={win.start} onChange={e => setWin(n, { start: e.target.value })}
                        style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)' }} />
                    </label>
                    <label style={{ fontSize: 12, color: 'var(--ind-tx2)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      to <input type="time" value={win.end} onChange={e => setWin(n, { end: e.target.value })}
                        style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)' }} />
                    </label>
                    <span className="ind-cost" style={{ background: '#ECFDF5', color: '#047857' }}>ENFORCED</span>
                  </div>
                )}
              </div>
            );
          })}

          {/* save bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <button className="ind-btn" style={{ background: 'var(--ind-accent)', color: '#fff', borderColor: 'var(--ind-accent)', fontWeight: 700 }}
              onClick={save} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save whitelist'}
            </button>
            {dirty && <span style={{ fontSize: 12, color: 'var(--ind-high)' }}>Unsaved changes</span>}
            {msg && <span style={{ fontSize: 12, color: msg.type === 'error' ? 'var(--ind-crit)' : 'var(--ind-ok)' }}>{msg.text}</span>}
          </div>
        </>
      )}
    </IndustrialLayout>
  );
}
